<script lang="ts">
import { useI18n } from 'vue-i18n';
import { ValidationRule } from 'quasar';
import { defineComponent, PropType } from 'vue';
import { CurrencyForAccountFragment } from '@/app/graphql/account/sdk.account';

import UiSelect from '@/shared/ui/select';
import UiEntityLogo from '@/shared/ui/entity-logo';

export default defineComponent({
  name: 'EntityOrderStatement',
  components: { UiSelect, UiEntityLogo },
  props: {
    modelValue: {
      type: Object as PropType<CurrencyForAccountFragment[]>,
      default: null,
    },
    options: {
      type: Array as PropType<CurrencyForAccountFragment[]>,
      default: () => [],
    },
    currency: {
      type: String,
      default: '',
    },
    rules: {
      type: Array as PropType<ValidationRule[]>,
      default: () => [],
    },
    label: {
      type: String,
      default: '',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    lazyRules: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    useInput: {
      type: Boolean,
      default: false,
    },
    emitValue: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    useChips: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'filter'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const onFilter = (val: string, update: Function) => {
      update();
      emit('filter', val, update);
    };
    const updateModelValue = (value: CurrencyForAccountFragment | CurrencyForAccountFragment[] | null) => {
      if (props.multiple && !value) return emit('update:modelValue', []);
      emit('update:modelValue', value);
    };

    const removeSelectedCurrency = (currency: CurrencyForAccountFragment | null) => {
      const currenciesForUpdate = props.modelValue.filter((value) => value.isoCode !== currency?.isoCode);
      updateModelValue(currenciesForUpdate);
    };

    return {
      t,
      onFilter,
      updateModelValue,
      removeSelectedCurrency,
    };
  },
});
</script>

<template>
  <ui-select
    :model-value="modelValue"
    :options="options"
    :label="label"
    :multiple="multiple"
    :use-chips="useChips"
    :emit-value="emitValue"
    :option-label="optionLabel"
    :option-value="optionValue"
    :clearable="clearable"
    :use-input="useInput"
    :disable="disable"
    :rules="rules"
    :lazy-rules="lazyRules"
    @update:model-value="updateModelValue"
    @filter="onFilter"
  >
    <template #option-left-content="opt">
      <ui-entity-logo :name="opt?.isoCode" />
    </template>
    <template #option-header>{{ t('SHARED.CURRENCY') }}</template>
    <template #option-body="opt">{{ opt?.isoCode }} </template>

    <template #selected-item="scope">
      <q-chip :tabindex="scope?.tabindex" removable style="background: #f6f7f8" @remove="scope?.remove()">
        <ui-entity-logo :name="scope?.opt?.isoCode || ''" size="xs" />
        {{ scope?.opt?.isoCode }}
      </q-chip>
    </template>
  </ui-select>
</template>
